<template>

    <validation-observer ref="compensationObserver" v-slot="{ invalid }">

        <v-row dense>

            <v-col cols="12">

                <v-alert v-if="infoText" type="info" class="pa-2 mb-2 body-2">
                    {{infoText}}
                </v-alert>

                <v-alert v-if="warningText" type="warning" icon="mdi-alert" class=" pa-2 mb-2 body-2">
                    {{warningText}}
                </v-alert>
            </v-col>

            <!-- REWARD -->
            <v-col cols="4">
                <title-card title="Rewards" :help="rewardHelp">

                    <validation-provider name="Reward completed"
                                         rules="required|integer|min_value:0|max_value:10000"
                                         v-slot="{errors}">
                        <v-text-field
                                class="pt-2"
                                v-model="data.reward_completed"
                                label="Reward completed"
                                :error-messages="errors"
                                @change="valuesChanged"
                        ></v-text-field>

                    </validation-provider>

                    <validation-provider name="Reward profile"
                                         rules="required|integer|min_value:0|max_value:10000"
                                         v-slot="{errors}">
                        <v-text-field
                                class="pt-2"
                                v-model="data.reward_profile"
                                label="Reward profile"
                                :error-messages="errors"
                                @change="valuesChanged"
                        ></v-text-field>

                    </validation-provider>

                    <validation-provider name="Reward quota"
                                         rules="required|integer|min_value:0|max_value:10000"
                                         v-slot="{errors}">
                        <v-text-field
                                class="pt-2"
                                v-model="data.reward_quota"
                                label="Reward quota"
                                name="reward quota"
                                :error-messages="errors"
                                @change="valuesChanged"
                        ></v-text-field>

                    </validation-provider>
                </title-card>
            </v-col>

            <!-- REVENUE -->
            <v-col cols="4">
                <title-card title="Revenue" :help="revenueHelp">

                    <validation-provider name="Revenue completed"
                                         rules="required|integer|min_value:0|max_value:10000"
                                         v-slot="{errors}">
                        <v-text-field
                                class="pt-2"
                                v-model="data.revenue_completed"
                                label="Revenue completed"
                                :error-messages="errors"
                                @change="valuesChanged"
                        ></v-text-field>

                    </validation-provider>

                    <validation-provider name="Revenue profile"
                                         rules="required|integer|min_value:0|max_value:10000"
                                         v-slot="{errors}">
                        <v-text-field
                                class="pt-2"
                                v-model="data.revenue_profile"
                                label="Revenue profile"
                                :error-messages="errors"
                                @change="valuesChanged"
                        ></v-text-field>

                    </validation-provider>

                    <validation-provider name="Revenue quota"
                                         rules="required|integer|min_value:0|max_value:10000"
                                         v-slot="{errors}">

                        <v-text-field
                                class="pt-2"
                                v-model="data.revenue_quota"
                                label="Revenue quota"
                                :error-messages="errors"
                                @change="valuesChanged"
                        ></v-text-field>

                    </validation-provider>

                </title-card>
            </v-col>

            <!-- NET REVENUE -->
            <v-col cols="4">

                <title-card title="Net revenue" color="green darken-4" :help="netRevenueHelp">

                    <v-text-field
                            :prepend-icon="netRevenueCompleted >= 0 ? 'mdi-check' : 'mdi-alert'"
                            class="pt-2"
                            :value="netRevenueCompleted"
                            label="Net revenue completed"
                            :readonly="true"
                    ></v-text-field>

                    <v-text-field
                            :prepend-icon="netRevenueProfile >= 0 ? 'mdi-check' : 'mdi-alert'"
                            class="pt-2"
                            :value="netRevenueProfile"
                            label="Revenue profile"
                            :readonly="true"
                    ></v-text-field>

                    <v-text-field
                            :prepend-icon="netRevenueQuota >= 0 ? 'mdi-check' : 'mdi-alert'"
                            class="pt-2"
                            :value="netRevenueQuota"
                            label="Revenue quota"
                            :readonly="true"
                    ></v-text-field>

                </title-card>
            </v-col>

        </v-row>

    </validation-observer>

</template>

<script>

    import TitleCard from "./TitleCard";

    export default {

        components: {
            TitleCard
        },

        props: {
            rewardCompleted: {
                type: Number | String,
                required: true,
            },
            rewardProfile: {
                type: Number | String,
                required: true,
            },
            rewardQuota: {
                type: Number | String,
                required: true,
            },
            revenueCompleted: {
                type: Number | String,
                required: true,
            },
            revenueProfile: {
                type: Number | String,
                required: true,
            },
            revenueQuota: {
                type: Number | String,
                required: true,
            },
            infoText: {
                type: String,
                required: false,
                default: null
            },
            warningText: {
                type: String,
                required: false,
                default: null,
            },
            rewardHelp: {
              type: String,
              required: false,
              default: null,
            },
            revenueHelp: {
              type: String,
              required: false,
              default: null,
            },
            netRevenueHelp: {
              type: String,
              required: false,
              default: null,
            }
        },

        watch: {
            revenueCompleted(value) {
                this.data.revenue_completed = value;
            },
            revenueProfile(value) {
                this.data.revenue_profile = value;
            },
            revenueQuota(value) {
                this.data.revenue_quota = value;
            },
            rewardCompleted(value) {
                this.data.reward_completed = value;
            },
            rewardProfile(value) {
                this.data.reward_profile = value;
            },
            rewardQuota(value) {
                this.data.reward_quota = value;
            },
        },

        data() {
            return {
                data: {
                    reward_completed: 0,
                    reward_quota: 0,
                    reward_profile: 0,
                    revenue_completed: 0,
                    revenue_quota: 0,
                    revenue_profile: 0,
                }
            }
        },

        computed: {
            netRevenueCompleted() {
                return this.data.revenue_completed - this.data.reward_completed;
            },
            netRevenueProfile() {
                return this.data.revenue_profile - this.data.reward_profile;
            },
            netRevenueQuota() {
                return this.data.revenue_quota - this.data.reward_quota;
            },
        },

        mounted() {
            this.data.revenue_completed = this.revenueCompleted;
            this.data.revenue_profile = this.revenueProfile;
            this.data.revenue_quota = this.revenueQuota;
            this.data.reward_completed = this.rewardCompleted;
            this.data.reward_profile = this.rewardProfile;
            this.data.reward_quota = this.rewardQuota;

            this.valuesChanged();
        },

        methods: {
            valuesChanged() {

                if (this.$refs.compensationObserver) {
                    this.$refs.compensationObserver.validate()
                        .then(isValid => {
                            if (isValid) {
                                this.$emit('change', this.data);
                                this.$emit('error', {name: 'tab7', error: false});
                            } else {
                                this.$emit('error', {name: 'tab7', error: true});
                            }
                        });
                }
            }
        },
    }
</script>

<style scoped>

</style>